import type { CreateFeedbackInput, FeedbackUploadInput } from "../../Feedback/feedbackApi.ts";

export const FEEDBACK_DRAFT_STORAGE_KEY = "movesofconsequence:feedback-draft:v1";

export interface FeedbackDraft {
	title: string;
	contentHtml: string;
	context: CreateFeedbackInput["context"] | null;
	uploads: FeedbackUploadInput[];
	minimized: boolean;
	updatedAt: number;
}

const isContext = (value: unknown): value is CreateFeedbackInput["context"] => {
	if (!value || typeof value !== "object" || Array.isArray(value)) return false;
	const context = value as Record<string, unknown>;
	return (
		typeof context.url === "string" &&
		typeof context.path === "string" &&
		typeof context.viewportWidth === "number" &&
		typeof context.viewportHeight === "number" &&
		typeof context.userAgent === "string" &&
		(context.matchId === undefined || typeof context.matchId === "string")
	);
};

const isUpload = (value: unknown): value is FeedbackUploadInput => {
	if (!value || typeof value !== "object" || Array.isArray(value)) return false;
	const upload = value as Record<string, unknown>;
	return (
		typeof upload.id === "string" &&
		typeof upload.name === "string" &&
		(upload.kind === "image" || upload.kind === "video" || upload.kind === "screenshot") &&
		typeof upload.dataUrl === "string"
	);
};

export const readFeedbackDraft = (storage: Storage = window.localStorage): FeedbackDraft | null => {
	try {
		const raw = storage.getItem(FEEDBACK_DRAFT_STORAGE_KEY);
		if (!raw) return null;
		const value = JSON.parse(raw) as Record<string, unknown>;
		if (
			typeof value.title !== "string" ||
			typeof value.contentHtml !== "string" ||
			(value.context !== null && !isContext(value.context)) ||
			!Array.isArray(value.uploads) ||
			!value.uploads.every(isUpload) ||
			typeof value.minimized !== "boolean" ||
			typeof value.updatedAt !== "number"
		) {
			return null;
		}
		return value as unknown as FeedbackDraft;
	} catch {
		return null;
	}
};

export const saveFeedbackDraft = (
	draft: FeedbackDraft,
	storage: Storage = window.localStorage,
): boolean => {
	try {
		storage.setItem(FEEDBACK_DRAFT_STORAGE_KEY, JSON.stringify(draft));
		return true;
	} catch {
		return false;
	}
};

export const clearFeedbackDraft = (storage: Storage = window.localStorage): void => {
	try {
		storage.removeItem(FEEDBACK_DRAFT_STORAGE_KEY);
	} catch {
		// A blocked storage implementation must not prevent closing or sending feedback.
	}
};
